
import * as React from "react"
import 'mapbox-gl/dist/mapbox-gl.css';
import mapboxgl from 'mapbox-gl';

const MOBILE_BREAKPOINT = 768

export interface MobileInfo {
  isMobile: boolean;
  orientation: 'portrait' | 'landscape';
}

export function useIsMobile(): MobileInfo {
  const [isMobile, setIsMobile] = React.useState<boolean>(window.innerWidth < MOBILE_BREAKPOINT)
  const [orientation, setOrientation] = React.useState<'portrait' | 'landscape'>(
    window.innerHeight > window.innerWidth ? 'portrait' : 'landscape'
  )

  React.useEffect(() => {
    // Handler function for media query and resize events
    const updateMobileStatus = () => {
      const width = window.innerWidth
      const height = window.innerHeight
      setIsMobile(width < MOBILE_BREAKPOINT)
      setOrientation(height > width ? 'portrait' : 'landscape')
    }
    
    // Use both matchMedia and window resize for better compatibility
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`)
    mql.addEventListener("change", updateMobileStatus)
    window.addEventListener("resize", updateMobileStatus)
    window.addEventListener("orientationchange", () => {
      // Delay to ensure dimensions have updated after orientation change
      setTimeout(updateMobileStatus, 100)
    })
    
    return () => {
      mql.removeEventListener("change", updateMobileStatus)
      window.removeEventListener("resize", updateMobileStatus)
      window.removeEventListener("orientationchange", updateMobileStatus)
    }
  }, [])

  return { isMobile, orientation };
}

// Add the missing useMobileAdjustments function that was referenced
export function useMobileAdjustments(map: mapboxgl.Map | null) {
  const { isMobile, orientation } = useIsMobile();
  
  React.useEffect(() => {
    if (!map || !isMobile) return;
    
    console.log("Applying mobile optimizations to map");
    
    // Check if map has been initialized properly
    if (!map.getContainer()) {
      console.log("Map container not ready yet");
      return;
    }
    
    try {
      // Adjust map for better mobile experience
      map.resize();
      
      // Use setTimeout to ensure the map properly adjusts after the UI has settled
      setTimeout(() => {
        if (!map || !map.getContainer()) return;
        
        // Set appropriate zoom level for mobile - FURTHER ADJUSTED for better Mexico view
        const currentZoom = map.getZoom();
        if (currentZoom < 3.5) {
          // Use a more zoomed out view to show all of Mexico
          const newZoom = orientation === 'portrait' ? 2.4 : 2.6;
          
          // Move center slightly to ensure Mexico is fully visible
          map.easeTo({
            center: [-101, 23], // Centered better on Mexico
            zoom: newZoom,
            duration: 1000
          });
        }
        
        // Make controls more touch-friendly
        const controlContainer = document.querySelector('.mapboxgl-ctrl-top-right');
        if (controlContainer) {
          const controls = controlContainer.querySelectorAll('button');
          controls.forEach(control => {
            if (control instanceof HTMLElement) {
              control.style.width = '44px';
              control.style.height = '44px';
            }
          });
        }
        
        // Ensure attribution control is visible but minimized
        const attributionControl = document.querySelector('.mapboxgl-ctrl-attrib');
        if (attributionControl && attributionControl instanceof HTMLElement) {
          attributionControl.style.fontSize = '8px';
        }
        
        // Ensure map is visible
        const mapContainer = map.getContainer();
        if (mapContainer) {
          mapContainer.style.visibility = 'visible';
          mapContainer.style.height = '100%';
          mapContainer.style.width = '100%';
        }
        
        console.log("Mobile map optimizations applied successfully");
      }, 500);
    } catch (error) {
      console.error("Error applying mobile optimizations:", error);
    }
  }, [map, isMobile, orientation]);
  
  return { isMobile, orientation };
}
